import { Injectable, inject } from '@angular/core';
import { Observable, EMPTY, of, expand, map, reduce, scan, catchError, throwError } from 'rxjs';
import { GraphService } from 'src/app/shared/lib/api/graph.service';
import {
    EgresoDetalle,
    EgresoDetalleConstanciaResponse,
    EgresoEstado,
    GetEgresoResponse,
    ControlDiarioEgresoReadModel,
    ControlDiarioPageResponse,
    EgresoOrderDetailsResponse,
} from '../models';
import {
    GET_EGRESO_DETALLE_CONSTANCIA,
    GET_EGRESO_DETALLE,
    GET_ALL_EGRESOS_TESORERIA_PAGINATED,
    GET_CONTROL_DIARIO_EGRESOS_PAGE,
    GET_EGRESO_ORDER_DETAILS,
    GET_EGRESOS_DISPONIBLES_PARA_VINCULACION,
    GET_INCOME_RECONCILIATION_PAYMENT_CANDIDATES,
    UPDATE_EGRESO,
    DELETE_EGRESO,
    CREATE_EGRESO,
    CHANGE_EGRESO_BANK_ACCOUNT,
} from '../graphql/egreso.queries';
import { CuentaBancoQuery } from 'src/app/shared/interfaces';
import { BankAccountApi } from 'src/app/entities/bank-account';

/**
 * Servicio para gestionar operaciones relacionadas con egresos de tesorería
 * 
 * Responsabilidades:
 * - Obtener detalles de un egreso incluyendo comprobantes de pago
 * - Mapear respuestas GraphQL a modelos tipados
 * - Proporcionar datos enriquecidos para la UI
 */
@Injectable({
    providedIn: 'root',
})
export class TesoreríaEgresoService {
    private readonly graphQL = inject(GraphService);
    private readonly bankAccountApi = inject(BankAccountApi);

    getOrderFilterOptions(ids: string[]): Observable<Array<{ id: string; number: string; fileNumber?: string }>> {
        if (!ids.length) return of([]);
        return this.graphQL.queryGraphQLCore<{
            getOrdersByIds: Array<{ orderId: string; orderNumber: string; fileSnapshot?: { fileNumber?: string } }>;
        }>({
            query: `query ControlDiarioOrdersByIds($orderIds: [ID!]!) {
                getOrdersByIds(orderIds: $orderIds) {
                    orderId orderNumber fileSnapshot { fileNumber }
                }
            }`,
            variables: { orderIds: ids },
        }).pipe(map(response => (response.getOrdersByIds ?? []).map(order => ({
            id: order.orderId,
            number: order.orderNumber,
            fileNumber: order.fileSnapshot?.fileNumber,
        }))));
    }

    getPurchaseOrderFilterOptions(ids: string[]): Observable<Array<{ id: string; number: string }>> {
        if (!ids.length) return of([]);
        return this.graphQL.queryGraphQL<{
            getOrdenesCompraByIds: Array<{ id: string; numeroOC: string }>;
        }>({
            query: `query ControlDiarioPurchaseOrdersByIds($ids: [String!]!) {
                getOrdenesCompraByIds(ids: $ids) { id numeroOC }
            }`,
            variables: { ids },
        }).pipe(map(response => (response.getOrdenesCompraByIds ?? []).map(order => ({
            id: order.id,
            number: order.numeroOC,
        }))));
    }

    /**
     * @description Obtiene los detalles de un egreso específico incluyendo su lista de comprobantes de pago
     * @param egresoId - ID del egreso a obtener
     * @returns Observable con los detalles del egreso y sus comprobantes
     */
    getEgresoDetalleConstancia(egresoId: string): Observable<EgresoDetalleConstanciaResponse> {
        return this.graphQL.queryGraphQL<{
            getEgresoDetalleConstancia: EgresoDetalleConstanciaResponse;
        }>({
            query: GET_EGRESO_DETALLE_CONSTANCIA,
            variables: { id: egresoId },
        }).pipe(
            map((response) => response.getEgresoDetalleConstancia),
            catchError(error => {
                console.error(`Error fetching egreso detalle for ID ${egresoId}:`, error);
                return throwError(() => new Error('Error al obtener detalles del egreso'));
            }),
        );
    }

    /**
     * Alias de compatibilidad para financial.service.ts
     */
    getEgreso(id: string): Observable<EgresoDetalle | null> {
        return this.graphQL.queryGraphQL<GetEgresoResponse>({
            query: GET_EGRESO_DETALLE,
            variables: { id },
        }).pipe(
            map((response) =>
                response.getEgreso
                    ? this.mapEgresoDetalle(response.getEgreso)
                    : null,
            ),
            catchError(error => {
                console.error(`Error fetching egreso detalle for ID ${id}:`, error);
                return throwError(() => new Error('Error al obtener detalles del egreso'));
            }),
        );
    }

    /**
     * @description Mapea un egreso bruto a un egreso detallado con validaciones
     * @param egreso - Egreso bruto del backend
     * @returns Egreso con tipos validados
     */
    mapEgresoDetalle(egreso: any): EgresoDetalle {
        return {
            id: egreso.id,
            hasUnresolvedOrderReferences: egreso.hasUnresolvedOrderReferences ?? false,
            unresolvedOrderIds: egreso.unresolvedOrderIds ?? [],
            comision: egreso.comision ?? 0,
            fechaPago: egreso.fechaPago,
            estado: egreso.estado,
            tipoOrden: egreso.tipoOrden,
            updatedAt: egreso.updatedAt,
            fechaReprogramacion: egreso.fechaReprogramacion,
            tipoPayment: egreso.tipoPayment,
            cargadoTelecredito: egreso.cargadoTelecredito ?? false,
            proveedor: egreso.proveedor,
            cuenta: egreso.cuenta,
            egresos: egreso.egresos ?? [],
            egresosSAF: egreso.egresosSAF ?? [],
            proofsOfPayment: egreso.proofsOfPayment ?? [],
        };
    }

    /**
     * @description Obtiene todos los egresos de tesorería con soporte para paginación automática
     * @returns Observable con los egresos agrupados en la propiedad getAllEgresosTesoreria
     */
    getEgresos(): Observable<any> {
        const limit = 500;

        return this.getEgresosPage(limit).pipe(
            expand((response: any) => {
                const page = response?.getAllEgresosTesoreriaPaginated;
                if (page?.hasMore && page?.nextToken) {
                    return this.getEgresosPage(limit, page.nextToken);
                }
                return EMPTY;
            }),
            map((response: any) => response?.getAllEgresosTesoreriaPaginated?.items ?? []),
            reduce((acc: any[], items: any[]) => [...acc, ...items], []),
            map((items: any[]) => ({ getAllEgresosTesoreria: items })),
        );
    }

    /**
     * @description Obtiene una página de egresos con paginación
     * @param limit - Cantidad de registros por página
     * @param nextToken - Token para obtener la siguiente página
     * @returns Observable con la página de egresos
     */
    getEgresosPage(
        limit: number = 100,
        nextToken?: string,
        cuentaBancoId?: string,
    ): Observable<any> {
        return this.graphQL.queryGraphQL<any>({
            query: GET_ALL_EGRESOS_TESORERIA_PAGINATED,
            variables: { limit, nextToken, cuentaBancoId },
        });
    }

    /**
     * Emite nuevamente la colección acumulada cada vez que llega una página.
     * Permite que las pantallas extensas publiquen resultados sin esperar el
     * recorrido completo del backend.
     */
    getEgresosProgressively(limit: number = 100): Observable<{
        getAllEgresosTesoreria: ControlDiarioEgresoReadModel[];
        hasMore: boolean;
    }> {
        const getPage = (nextToken?: string) => this.graphQL.queryGraphQL<ControlDiarioPageResponse>({
            query: GET_CONTROL_DIARIO_EGRESOS_PAGE,
            variables: { limit, nextToken },
        });
        return getPage().pipe(
            expand((response) => {
                const page = response?.getAllEgresosTesoreriaSummaryPaginated;
                return page?.hasMore && page?.nextToken
                    ? getPage(page.nextToken)
                    : EMPTY;
            }),
            map((response) => {
                const page = response?.getAllEgresosTesoreriaSummaryPaginated ?? {};
                return {
                    items: page.items ?? [],
                    hasMore: !!page.hasMore,
                };
            }),
            scan(
                (acc, page) => ({
                    items: [...acc.items, ...page.items],
                    hasMore: page.hasMore,
                }),
                { items: [] as ControlDiarioEgresoReadModel[], hasMore: true },
            ),
            map((page) => ({
                getAllEgresosTesoreria: page.items,
                hasMore: page.hasMore,
            })),
        );
    }

    getEgresoOrderDetails(id: string) {
        return this.graphQL.queryGraphQL<EgresoOrderDetailsResponse>({
            query: GET_EGRESO_ORDER_DETAILS,
            variables: { id },
        }).pipe(map(response => response.getEgresoOrderDetails));
    }

    getEgresosDisponiblesParaVinculacionPage(
        cuentaBancoId: string,
        limit: number = 50,
        nextToken?: string,
    ): Observable<any> {
        return this.graphQL.queryGraphQL<any>({
            query: GET_EGRESOS_DISPONIBLES_PARA_VINCULACION,
            variables: { cuentaBancoId, limit, nextToken },
        });
    }

    /**
     * Emite páginas útiles de candidatos para el modal Vincular Pago. El
     * backend absorbe los Scan vacíos durante la fase previa al índice.
     */
    getEgresosDisponiblesParaVinculacionProgressively(
        cuentaBancoId: string,
        limit: number = 50,
    ): Observable<{ getAllEgresosTesoreria: any[]; hasMore: boolean }> {
        return this.getEgresosDisponiblesParaVinculacionPage(
            cuentaBancoId,
            limit,
        ).pipe(
            expand((response: any) => {
                const page = response?.getEgresosDisponiblesParaVinculacion;
                return page?.hasMore && page?.nextToken
                    ? this.getEgresosDisponiblesParaVinculacionPage(
                        cuentaBancoId,
                        limit,
                        page.nextToken,
                    )
                    : EMPTY;
            }),
            map((response: any) => {
                const page = response?.getEgresosDisponiblesParaVinculacion ?? {};
                return { items: page.items ?? [], hasMore: !!page.hasMore };
            }),
            scan(
                (acc, page) => ({
                    items: [...acc.items, ...page.items],
                    hasMore: page.hasMore,
                }),
                { items: [] as any[], hasMore: true },
            ),
            map(page => ({
                getAllEgresosTesoreria: page.items,
                hasMore: page.hasMore,
            })),
        );
    }

    getIncomeReconciliationPaymentCandidatesPage(
        cuentaBancoId: string,
        limit: number = 100,
        nextToken?: string,
    ): Observable<any> {
        return this.graphQL.queryGraphQL<any>({
            query: GET_INCOME_RECONCILIATION_PAYMENT_CANDIDATES,
            variables: { cuentaBancoId, limit, nextToken },
        });
    }

    getIncomeReconciliationPaymentCandidatesProgressively(
        cuentaBancoId: string,
        limit: number = 100,
    ): Observable<{ getAllEgresosTesoreria: any[]; hasMore: boolean }> {
        return this.getIncomeReconciliationPaymentCandidatesPage(
            cuentaBancoId,
            limit,
        ).pipe(
            expand((response: any) => {
                const page = response?.getIncomeReconciliationPaymentCandidates;
                return page?.hasMore && page?.nextToken
                    ? this.getIncomeReconciliationPaymentCandidatesPage(
                        cuentaBancoId,
                        limit,
                        page.nextToken,
                    )
                    : EMPTY;
            }),
            map((response: any) => {
                const page = response?.getIncomeReconciliationPaymentCandidates ?? {};
                return { items: page.items ?? [], hasMore: !!page.hasMore };
            }),
            scan(
                (acc, page) => ({
                    items: [...acc.items, ...page.items],
                    hasMore: page.hasMore,
                }),
                { items: [] as any[], hasMore: true },
            ),
            map(page => ({
                getAllEgresosTesoreria: page.items,
                hasMore: page.hasMore,
            })),
        );
    }

    /**
     * Carga candidatos de una cuenta para la vinculación de pagos.
     * Actualmente el backend usa un filtro temporal sobre Scan; esta firma se
     * mantiene para reemplazarlo por la consulta indexada sin cambiar la UI.
     */
    getEgresosByBankProgressively(cuentaBancoId: string, limit: number = 100): Observable<{
        getAllEgresosTesoreria: any[];
        hasMore: boolean;
    }> {
        return this.getEgresosPage(limit, undefined, cuentaBancoId).pipe(
            expand((response: any) => {
                const page = response?.getAllEgresosTesoreriaPaginated;
                return page?.hasMore && page?.nextToken
                    ? this.getEgresosPage(limit, page.nextToken, cuentaBancoId)
                    : EMPTY;
            }),
            map((response: any) => {
                const page = response?.getAllEgresosTesoreriaPaginated ?? {};
                return {
                    items: page.items ?? [],
                    hasMore: !!page.hasMore,
                };
            }),
            scan(
                (acc, page) => ({
                    items: [...acc.items, ...page.items],
                    hasMore: page.hasMore,
                }),
                { items: [] as any[], hasMore: true },
            ),
            map((page) => ({
                getAllEgresosTesoreria: page.items,
                hasMore: page.hasMore,
            })),
        );
    }

    /**
     * @description Obtiene todas las cuentas bancarias
     * @returns Observable con la lista de cuentas
     */
    getCuentas(): Observable<{ getAllCuentas: CuentaBancoQuery[] }> {
        return this.bankAccountApi.getAll();
    }

    /**
     * @description Obtiene por cuenta la fecha de carga del último movimiento bancario
     * @returns Observable con la lista de último movimiento por cuenta
     */
    getLastMovementsByAccount() {
        return this.bankAccountApi.getLastMovements();
    }

    /**
     * @description Actualiza un egreso existente
     * @param id - ID del egreso a actualizar
     * @param egreso - Datos del egreso a actualizar
     * @returns Observable con el resultado de la actualización
     */
    updateEgreso(id: string, egreso: any): Observable<any> {
        return this.graphQL.mutateGraphQL<any>({
            mutation: UPDATE_EGRESO,
            variables: { id, egreso },
        });
    }

    changeEgresoBankAccount(input: {
        egresoId: string;
        cuentaBancoId: string;
        expectedCuentaBancoId?: string;
    }): Observable<{
        id: string;
        updatedAt?: string;
        cuenta?: CuentaBancoQuery;
    }> {
        return this.graphQL.mutateGraphQL<{
            changeEgresoBankAccount: {
                id: string;
                updatedAt?: string;
                cuenta?: CuentaBancoQuery;
            };
        }>({
            mutation: CHANGE_EGRESO_BANK_ACCOUNT,
            variables: { input },
        }).pipe(map(response => response.changeEgresoBankAccount));
    }

    /**
     * @description Crea un nuevo egreso
     * @param egreso - Datos del egreso a crear
     * @returns Observable con el resultado de la creación
     */
    createEgreso(egreso: any): Observable<any> {
        return this.graphQL.mutateGraphQL<any>({
            mutation: CREATE_EGRESO,
            variables: { egreso },
        });
    }

    /**
     * @description Elimina un egreso
     * @param id - ID del egreso a eliminar
     * @returns Observable con el resultado de la eliminación
     */
    deleteEgreso(id: string): Observable<any> {
        return this.graphQL.mutateGraphQL<any>({
            mutation: DELETE_EGRESO,
            variables: { id },
        });
    }

    /**
     * @description Cambia el estado de un egreso
     * @param id - ID del egreso
     * @param tipoEstado - Nuevo estado del egreso
     * @param tipo - Tipo de cambio de estado
     * @returns Observable con el resultado del cambio de estado
     */
    changeEstadoEgreso(id: string, tipoEstado: EgresoEstado, tipo: string): Observable<any> {
        return this.graphQL.mutateGraphQL<any>({
            mutation: `mutation changeEstadoEgreso($id: String!, $tipoEstado: String!, $tipo: String!){
                changeEstadoEgreso(id: $id, tipoEstado:$tipoEstado, tipo: $tipo){
                    id,
                    estado
                }
            }`,
            variables: { id, tipoEstado, tipo },
        });
    }

    /**
     * @description Cambia la fecha de pago programada de un egreso
     * @param id - ID del egreso
     * @param fechaReprogramacion - Nueva fecha de pago
     * @returns Observable con el resultado del cambio de fecha
     */
    changeFechaPagoEgreso(id: string, fechaReprogramacion: string): Observable<any> {
        return this.graphQL.mutateGraphQL<any>({
            mutation: `mutation changeFechaPagoEgreso($id: String!, $fechaReprogramacion: String!){
                changeFechaPagoEgreso(id: $id, fechaReprogramacion: $fechaReprogramacion){
                    id,
                }
            }`,
            variables: { id, fechaReprogramacion },
        });
    }

    updateSaldoBanco(nombre: string, monto: number) {
        return this.graphQL.mutateGraphQL<any>({
            mutation: `mutation updateSaldoBanco($nombre:String! , $monto:Float! ){
                updateSaldoBanco(nombre:$nombre, monto:$monto ){
                id}
            }`,
            variables: { nombre, monto },
        });
    }

    getAllSaldosCuentaByDate(date: string): Observable<any> {
        return this.graphQL.queryGraphQL<any>({
            query: `query getAllSaldosCuentaByDate($date:String!){
                getAllSaldosCuentaByDate(date:$date){
                    id,
                    saldo,
                    cuentaBancoId,
                    createdAt,
                }
            }`,
            variables: { date },
        });
    }
}
